/**
 * EncuestaModal — wizard interactivo de la encuesta de evaluación.
 *
 * Flujo: intro → una pantalla por sección (con validación de requeridas) →
 * envío vía RPC `encuesta_submit` → pantalla de agradecimiento.
 * "Responder después" cierra y pospone (snooze del EncuestaLauncher).
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { getSupabase } from '@aosenuma/lib-supabase';
import type {
  Encuesta,
  EncuestaPregunta,
  EncuestaSeccion,
  EncuestaSubmitItem,
  EncuestaSubmitResult,
  EncuestaValor,
} from '@aosenuma/lib-types';
import { PreguntaInput } from './inputs';

export interface EncuestaModalProps {
  encuesta: Encuesta;
  secciones: EncuestaSeccion[];
  preguntas: EncuestaPregunta[];
  /** "Responder después" — el launcher pospone (snooze). */
  onPostpone: () => void;
  /** Respuesta enviada con éxito (o ya existía). */
  onSubmitted: () => void;
}

type Answers = Record<string, EncuestaValor | undefined>;

export function EncuestaModal({
  encuesta,
  secciones,
  preguntas,
  onPostpone,
  onSubmitted,
}: EncuestaModalProps): ReactNode {
  // step: 0 = intro · 1..n = secciones · n+1 = gracias
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [touchedNext, setTouchedNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const startedAt = useRef<number>(Date.now());

  // Solo secciones con preguntas visibles para esta audiencia.
  const seccionesVisibles = useMemo(
    () => secciones.filter((s) => preguntas.some((q) => q.seccion_id === s.id)),
    [secciones, preguntas],
  );
  const totalSteps = seccionesVisibles.length;
  const seccionActual = step >= 1 && step <= totalSteps ? seccionesVisibles[step - 1] : undefined;
  const preguntasSeccion = useMemo(
    () => (seccionActual ? preguntas.filter((q) => q.seccion_id === seccionActual.id) : []),
    [preguntas, seccionActual],
  );

  const faltantesSeccion = preguntasSeccion.filter(
    (q) => q.requerida && answers[q.id] === undefined,
  );
  const respondidas = preguntas.filter((q) => answers[q.id] !== undefined).length;
  const progreso = step === 0 ? 0 : Math.min(1, respondidas / Math.max(1, preguntas.length));

  function setValor(preguntaId: string, valor: EncuestaValor | undefined): void {
    setAnswers((prev) => ({ ...prev, [preguntaId]: valor }));
    setError(null);
  }

  function goNext(): void {
    if (seccionActual && faltantesSeccion.length > 0) {
      setTouchedNext(true);
      return;
    }
    setTouchedNext(false);
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      void submit();
    }
  }

  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const items: EncuestaSubmitItem[] = preguntas.map((q) => ({
        pregunta_id: q.id,
        valor: answers[q.id] ?? null,
      }));
      const duracion = Math.round((Date.now() - startedAt.current) / 1000);
      const sb = getSupabase();
      const { data, error: rpcErr } = await sb.rpc('encuesta_submit', {
        p_encuesta_id: encuesta.id,
        p_items: items,
        p_duracion_segundos: duracion,
      });
      if (rpcErr) throw rpcErr;
      const result = data as EncuestaSubmitResult | null;
      if (!result?.ok) throw new Error('Respuesta no confirmada por el servidor.');
      setStep(totalSteps + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error inesperado al enviar.');
    } finally {
      setSubmitting(false);
    }
  }

  const esUltima = step === totalSteps;

  return (
    <div className="aos-enc-overlay" role="dialog" aria-modal="true" aria-labelledby="aos-enc-title">
      <style>{STYLES}</style>
      <div className="aos-enc-card">
        {/* Barra de progreso */}
        <div className="aos-enc-progress" aria-hidden="true">
          <div className="aos-enc-progress-fill" style={{ width: `${Math.round(progreso * 100)}%` }} />
        </div>

        {step === 0 && (
          <div className="aos-enc-body">
            <div className="aos-enc-kicker">Evaluación</div>
            <h2 id="aos-enc-title" className="aos-enc-title">{encuesta.titulo}</h2>
            {encuesta.descripcion && <p className="aos-enc-desc">{encuesta.descripcion}</p>}
            <ol className="aos-enc-agenda">
              {seccionesVisibles.map((s) => (
                <li key={s.id}>{s.titulo}</li>
              ))}
            </ol>
            <div className="aos-enc-actions">
              <button type="button" className="aos-enc-btn-secondary" onClick={onPostpone}>
                Responder después
              </button>
              <button type="button" className="aos-enc-btn-primary" onClick={() => setStep(1)}>
                Comenzar
              </button>
            </div>
          </div>
        )}

        {seccionActual && (
          <div className="aos-enc-body" key={seccionActual.id}>
            <div className="aos-enc-kicker">
              Sección {step} de {totalSteps}
            </div>
            <h2 id="aos-enc-title" className="aos-enc-title">{seccionActual.titulo}</h2>
            {seccionActual.descripcion && <p className="aos-enc-desc">{seccionActual.descripcion}</p>}

            <div className="aos-enc-preguntas">
              {preguntasSeccion.map((q) => {
                const falta = touchedNext && q.requerida && answers[q.id] === undefined;
                return (
                  <div key={q.id} className={`aos-enc-pregunta${falta ? ' is-missing' : ''}`}>
                    <div className="aos-enc-pregunta-texto">
                      {q.texto}
                      {!q.requerida && <span className="aos-enc-opcional"> · opcional</span>}
                    </div>
                    {q.ayuda && <div className="aos-enc-ayuda">{q.ayuda}</div>}
                    <PreguntaInput pregunta={q} valor={answers[q.id]} onChange={(v) => setValor(q.id, v)} />
                    {falta && <div className="aos-enc-falta">Esta pregunta es necesaria para continuar.</div>}
                  </div>
                );
              })}
            </div>

            {error && (
              <div role="alert" className="aos-enc-error">{error}</div>
            )}

            <div className="aos-enc-actions">
              <button type="button" className="aos-enc-btn-ghost" onClick={onPostpone}>
                Responder después
              </button>
              <div className="aos-enc-actions-right">
                {step > 1 && (
                  <button type="button" className="aos-enc-btn-secondary" onClick={() => setStep(step - 1)}>
                    Anterior
                  </button>
                )}
                <button
                  type="button"
                  className="aos-enc-btn-primary"
                  disabled={submitting}
                  onClick={goNext}
                >
                  {esUltima ? (submitting ? 'Enviando…' : 'Enviar evaluación') : 'Siguiente'}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === totalSteps + 1 && (
          <div className="aos-enc-body aos-enc-gracias">
            <div className="aos-enc-check" aria-hidden="true">✓</div>
            <h2 id="aos-enc-title" className="aos-enc-title">Gracias por tu evaluación</h2>
            <p className="aos-enc-desc">
              Tu retroalimentación quedó registrada y alimenta el reporte de cierre del proyecto y la
              mejora de proyectos futuros.
            </p>
            <div className="aos-enc-actions" style={{ justifyContent: 'center' }}>
              <button type="button" className="aos-enc-btn-primary" onClick={onSubmitted}>
                Continuar a la plataforma
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const STYLES = `
.aos-enc-overlay{position:fixed;inset:0;z-index:var(--z-modal,9000);display:grid;place-items:center;
  padding:16px;background:rgba(4,10,14,.62);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);}
.aos-enc-card{width:min(640px,100%);max-height:min(86dvh,760px);overflow-y:auto;position:relative;
  background:var(--bg-mid,#131a1f);border:1px solid var(--border,rgba(255,255,255,.12));
  border-radius:var(--radius-card,14px);color:var(--text,rgba(255,255,255,.92));
  font-family:Inter,system-ui,sans-serif;box-shadow:0 24px 80px rgba(0,0,0,.5);
  animation:aosEncIn .28s cubic-bezier(.2,.8,.2,1);}
@keyframes aosEncIn{from{opacity:0;transform:translateY(14px) scale(.985)}to{opacity:1;transform:none}}
.aos-enc-progress{position:sticky;top:0;height:4px;background:rgba(255,255,255,.08);z-index:2;}
.aos-enc-progress-fill{height:100%;background:var(--btn-primary-bg,#b4c96f);transition:width .35s ease;}
.aos-enc-body{padding:26px 28px 24px;display:flex;flex-direction:column;gap:12px;}
.aos-enc-kicker{font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--text-dim,rgba(255,255,255,.55));font-weight:600;}
.aos-enc-title{margin:0;font-size:21px;font-weight:650;line-height:1.25;}
.aos-enc-desc{margin:0;font-size:13.5px;line-height:1.55;color:var(--text-mid,rgba(255,255,255,.72));}
.aos-enc-agenda{margin:6px 0 4px;padding-left:20px;display:flex;flex-direction:column;gap:5px;
  font-size:13px;color:var(--text-mid,rgba(255,255,255,.72));}
.aos-enc-preguntas{display:flex;flex-direction:column;gap:18px;margin-top:4px;}
.aos-enc-pregunta{padding:14px 16px;border:1px solid var(--border,rgba(255,255,255,.1));
  border-radius:12px;background:rgba(255,255,255,.025);transition:border-color .18s ease;}
.aos-enc-pregunta.is-missing{border-color:#e08a7a;}
.aos-enc-pregunta-texto{font-size:14px;font-weight:560;line-height:1.45;margin-bottom:10px;}
.aos-enc-opcional{font-size:11.5px;font-weight:400;color:var(--text-faint,rgba(255,255,255,.4));}
.aos-enc-ayuda{font-size:12px;color:var(--text-dim,rgba(255,255,255,.55));margin:-6px 0 10px;}
.aos-enc-falta{font-size:12px;color:#e08a7a;margin-top:8px;}
.aos-enc-error{font-size:12.5px;color:#e08a7a;border:1px solid rgba(224,138,122,.4);
  border-radius:8px;padding:8px 12px;background:rgba(224,138,122,.08);}
.aos-enc-escala{display:flex;gap:8px;}
.aos-enc-escala-btn{flex:1;height:44px;border-radius:10px;border:1px solid var(--border,rgba(255,255,255,.14));
  background:rgba(255,255,255,.03);color:var(--text,rgba(255,255,255,.9));font-size:15px;font-weight:600;
  cursor:pointer;transition:all .15s ease;}
.aos-enc-escala-btn:hover{border-color:var(--btn-primary-bg,#b4c96f);transform:translateY(-1px);}
.aos-enc-escala-btn.is-active{background:var(--btn-primary-bg,#b4c96f);color:#0c1216;border-color:transparent;}
.aos-enc-escala-caption{margin-top:8px;font-size:11.5px;color:var(--text-dim,rgba(255,255,255,.5));min-height:14px;}
.aos-enc-nps{display:flex;gap:5px;flex-wrap:wrap;}
.aos-enc-nps-btn{width:40px;height:38px;border-radius:9px;border:1px solid var(--border,rgba(255,255,255,.14));
  background:rgba(255,255,255,.03);color:var(--text,rgba(255,255,255,.9));font-size:13px;font-weight:600;
  cursor:pointer;transition:all .15s ease;}
.aos-enc-nps-btn:hover{border-color:var(--btn-primary-bg,#b4c96f);transform:translateY(-1px);}
.aos-enc-nps-btn.is-active[data-zone=detractor]{background:#d98a6a;color:#0c1216;border-color:transparent;}
.aos-enc-nps-btn.is-active[data-zone=pasivo]{background:#d9c46a;color:#0c1216;border-color:transparent;}
.aos-enc-nps-btn.is-active[data-zone=promotor]{background:var(--btn-primary-bg,#b4c96f);color:#0c1216;border-color:transparent;}
.aos-enc-opciones{display:flex;flex-wrap:wrap;gap:8px;}
.aos-enc-chip{padding:9px 14px;border-radius:var(--radius-pill,999px);border:1px solid var(--border,rgba(255,255,255,.14));
  background:rgba(255,255,255,.03);color:var(--text,rgba(255,255,255,.88));font-size:12.5px;cursor:pointer;
  transition:all .15s ease;text-align:left;}
.aos-enc-chip:hover{border-color:var(--btn-primary-bg,#b4c96f);}
.aos-enc-chip.is-active{background:var(--btn-primary-bg,#b4c96f);color:#0c1216;border-color:transparent;font-weight:600;}
.aos-enc-chip-mark{font-weight:700;}
.aos-enc-sino{display:flex;gap:10px;}
.aos-enc-sino-btn{flex:1;max-width:140px;height:42px;border-radius:10px;border:1px solid var(--border,rgba(255,255,255,.14));
  background:rgba(255,255,255,.03);color:var(--text,rgba(255,255,255,.9));font-size:14px;font-weight:600;
  cursor:pointer;transition:all .15s ease;}
.aos-enc-sino-btn:hover{border-color:var(--btn-primary-bg,#b4c96f);}
.aos-enc-sino-btn.is-active{background:var(--btn-primary-bg,#b4c96f);color:#0c1216;border-color:transparent;}
.aos-enc-textarea{width:100%;box-sizing:border-box;border-radius:10px;padding:10px 12px;resize:vertical;
  border:1px solid var(--border,rgba(255,255,255,.16));background:rgba(255,255,255,.04);
  color:var(--text,rgba(255,255,255,.92));font-size:13px;font-family:inherit;line-height:1.5;min-height:72px;}
.aos-enc-textarea:focus{outline:none;border-color:var(--btn-primary-bg,#b4c96f);}
.aos-enc-actions{display:flex;justify-content:space-between;align-items:center;gap:10px;margin-top:10px;}
.aos-enc-actions-right{display:flex;gap:10px;}
.aos-enc-btn-primary{padding:11px 20px;border:none;border-radius:10px;font-size:13.5px;font-weight:650;cursor:pointer;
  background:var(--btn-primary-bg,#b4c96f);color:#0c1216;transition:filter .15s ease;}
.aos-enc-btn-primary:hover{filter:brightness(1.07);}
.aos-enc-btn-primary:disabled{opacity:.6;cursor:default;}
.aos-enc-btn-secondary{padding:11px 18px;border-radius:10px;font-size:13px;font-weight:500;cursor:pointer;
  background:transparent;color:var(--text,rgba(255,255,255,.85));border:1px solid var(--border-strong,rgba(255,255,255,.22));}
.aos-enc-btn-ghost{padding:11px 6px;border:none;background:transparent;font-size:12.5px;cursor:pointer;
  color:var(--text-dim,rgba(255,255,255,.5));text-decoration:underline;text-underline-offset:3px;}
.aos-enc-btn-ghost:hover{color:var(--text,rgba(255,255,255,.85));}
.aos-enc-gracias{align-items:center;text-align:center;padding-top:40px;padding-bottom:36px;}
.aos-enc-check{width:56px;height:56px;border-radius:50%;display:grid;place-items:center;font-size:26px;font-weight:700;
  background:var(--btn-primary-bg,#b4c96f);color:#0c1216;animation:aosEncPop .4s cubic-bezier(.2,.8,.2,1);}
@keyframes aosEncPop{from{transform:scale(.4);opacity:0}to{transform:none;opacity:1}}
@media (max-width:560px){
  .aos-enc-body{padding:20px 18px 18px;}
  .aos-enc-nps-btn{width:34px;height:34px;}
  .aos-enc-actions{flex-wrap:wrap;}
}
`;
